import { useState } from 'react'
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useRequestRunCancellation } from '../../hooks/useRunCancellation'
import {
  CANCEL_NOTE_MAX_LENGTH,
  cancelConfirmEnabled,
  cancelErrorText,
  cancelModalCopy,
  cancelToast,
} from './cancelFormat'
import type { EtlRunCancellation } from '../../types/etlRunCancellation'

/**
 * Asks Automaton to stop a running run — Observatory's third Bearer-pinned write, beside Run and Clear halt, and
 * built on the clear-halt dialog's pattern.
 *
 * Opened only from CancelRunAction, which offers it only when the server says `canCancelRuns`, the request table
 * exists and no request is open. It says what the run is, what a cancel stops (the WHOLE run), how fast and where it
 * lands, what stays and what goes, and that the run is never resumed — each of those verified against the code
 * (cancelFormat.ts says where). A note is required.
 *
 * An error keeps the dialog open with the note intact, so a retry is one click; success closes it, says what the
 * server answered — including "someone else already asked; your note was not recorded" — and hands the request to the
 * caller, which polls it until Automaton decides it.
 */
export function CancelRunModal({
  opened,
  runId,
  runKey,
  stale,
  scope,
  onClose,
  onSubmitted,
}: {
  opened: boolean
  /** The run (EtlRun id) to cancel. */
  runId: string
  runKey: string
  /** The run is stale: Automaton may close it rather than stop anything. */
  stale: boolean
  /** What the cancel stops, from the run's own detail (cancelFormat.runScopeText). */
  scope: string
  onClose: () => void
  /** The server's answer — a new request, or the one already open. */
  onSubmitted: (response: EtlRunCancellation) => void
}) {
  const cancel = useRequestRunCancellation()
  const [note, setNote] = useState('')

  const canConfirm = cancelConfirmEnabled(note) && !cancel.isPending
  const error = cancel.isError ? cancelErrorText(cancel.error) : null

  const close = () => {
    cancel.reset()
    onClose()
  }

  if (!opened) {
    return <Modal opened={false} onClose={close} title="Cancel run" />
  }

  const submit = () => {
    if (!canConfirm) return
    cancel.mutate(
      { runId, note: note.trim() },
      {
        onSuccess: (response) => {
          const { title, message, color } = cancelToast(response)
          notifications.show({ title, message, color })
          onSubmitted(response)
          close()
        },
      },
    )
  }

  return (
    <Modal opened={opened} onClose={close} title={`Cancel run ${runKey}`} size="lg">
      <Stack gap="md" data-dialog="cancel-run">
        <Text size="sm" fw={500}>
          {scope}
        </Text>

        <Stack gap="xs">
          {cancelModalCopy(runKey, stale).map((paragraph) => (
            <Text key={paragraph} size="sm">
              {paragraph}
            </Text>
          ))}
        </Stack>

        <Textarea
          label="Why it is being cancelled"
          description={
            'Required. Recorded with the request, so whoever meets the cancelled run next knows why. Shown only to '
            + 'administrators, and written to neither the run\'s error nor the Hangfire job.'
          }
          required
          autosize
          minRows={2}
          maxLength={CANCEL_NOTE_MAX_LENGTH}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />

        {error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title="The cancel request failed">
            <Text size="sm">{error}</Text>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Keep it running
          </Button>
          <Button color="red" loading={cancel.isPending} disabled={!canConfirm} onClick={submit}>
            Cancel this run
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
